/**
 * Test-landing bridge-framing experiment (fun vs competition) — shared
 * constants + storage helpers. (Supersedes the lean-hero experiment TEX-1637;
 * see _docs/experiments/2026-06-test-landing-bridge.md.)
 *
 * Assignment happens BEFORE this module ever runs: a pre-paint inline script
 * in `index.html` picks one uniform-random variant per browser, persists it in
 * localStorage and mirrors it onto `<html data-tl-variant="…">` so the inline
 * variant-gate `<style>` can show the right prerendered body on the very
 * first paint. This module is the typed, importable view of that contract —
 * keep `TL_VARIANT_STORAGE_KEY` / `TL_VARIANTS` in sync with the inline
 * script (a drift-guard test greps index.html for them).
 *
 * Assignment ≠ exposure: every visitor gets an assignment (the script runs on
 * all pages), but only visitors who actually render a test landing page get an
 * exposure stamp — and only stamped visitors carry the variant on their Lead.
 *
 * Deliberately posthog-free (pure DOM/storage) so it can be imported anywhere
 * — including `analytics.ts` itself — without import cycles.
 */

/** PostHog multivariate feature-flag key (see _docs/experiments/2026-06-test-landing-bridge.md). */
export const TL_FLAG_KEY = "test-landing-bridge";

/** localStorage key holding the assigned variant. Written by the index.html
 * pre-paint script; underscore (not `sa:`) so it stays cookie-name-safe if a
 * cookie mirror is ever needed (precedent: `sa_attribution`). The `_2` suffix
 * is an experiment-generation bump: the prior experiment's arms (`outcome`,
 * `fun`) no longer exist, so a returning visitor's stale `sa_tl_variant` would
 * fail `isTlVariant` and silently collapse into `control` — biasing the new
 * baseline. A fresh key re-randomizes every visitor cleanly into the new
 * control/competition split (old keys are harmless leftovers). */
export const TL_VARIANT_STORAGE_KEY = "sa_tl_variant_2";

/** localStorage key holding the exposure stamp (JSON: { test_slug, variant, t }).
 * Bumped to `_2` alongside the variant key so exposure is re-stamped under the
 * new experiment (see above). */
export const TL_EXPOSURE_STORAGE_KEY = "sa_tl_exposed_2";

/** The experiment arms (bridge framing: fun vs competition). `control` renders
 * the proven `fun-bridge` page (the prior experiment's winner, now the
 * baseline); `competition` reframes the same bridge layout around competing.
 * Order matters only to the inline script's random index; `control` must stay
 * first (PostHog experiments require a `control` variant and the CSS gate
 * defaults to it). */
export const TL_VARIANTS = ["control", "competition"] as const;

export type TlVariant = (typeof TL_VARIANTS)[number];

export function isTlVariant(v: unknown): v is TlVariant {
  return typeof v === "string" && (TL_VARIANTS as readonly string[]).includes(v);
}

/**
 * The variant the pre-paint script assigned to this browser: localStorage
 * first, then the `<html data-tl-variant>` attribute (covers blocked storage
 * within the current pageview — the attribute is set even when `setItem`
 * fails), else `null` (script failed entirely / non-browser context).
 */
export function readAssignedVariant(): TlVariant | null {
  try {
    const stored = localStorage.getItem(TL_VARIANT_STORAGE_KEY);
    if (isTlVariant(stored)) return stored;
  } catch {
    // localStorage blocked — fall through to the DOM mirror.
  }
  try {
    const attr = document.documentElement.getAttribute("data-tl-variant");
    if (isTlVariant(attr)) return attr;
  } catch {
    // No DOM either — genuinely unassigned.
  }
  return null;
}

export type TlExposure = { test_slug: string; variant: TlVariant };

/**
 * Record that this browser actually saw a test landing page (and which arm).
 * First exposure wins — re-stamping on later visits/slugs would let a Lead
 * disagree with the arm that did the persuading. Idempotent (StrictMode-safe).
 * localStorage on purpose: a visitor exposed Monday who signs up Thursday
 * still gets the variant attached to their Lead.
 */
export function stampExposure(testSlug: string, variant: TlVariant): void {
  try {
    if (getExposure()) return;
    localStorage.setItem(
      TL_EXPOSURE_STORAGE_KEY,
      JSON.stringify({ test_slug: testSlug, variant, t: Date.now() }),
    );
  } catch {
    // Storage blocked — Lead simply won't carry a variant for this visitor.
  }
}

/** The stamped exposure, or null when absent/garbled. */
export function getExposure(): TlExposure | null {
  try {
    const raw = localStorage.getItem(TL_EXPOSURE_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const { test_slug, variant } = parsed as Record<string, unknown>;
    if (typeof test_slug !== "string" || !test_slug || !isTlVariant(variant)) return null;
    return { test_slug, variant };
  } catch {
    return null;
  }
}
